import React, { useState, useEffect } from "react";
import { X, Layers } from "lucide-react";
import "./AddScriptModal.css";
import { toast } from "react-hot-toast";
import AuthService from "../../services/Auth.service";
import ApiService from "../../services/Api.service";
import AceEditor from "react-ace";

// Ace Editor setup
import "ace-builds/src-noconflict/mode-markdown";
import "ace-builds/src-noconflict/theme-github";

const AddScriptModal = ({ isOpen, onClose, companyId, campaignId, onSave, scriptToEdit}) => {
  const [script_name, setScriptName] = useState("");
  const [script_description, setScriptDescription] = useState("");
  const [greeting, setGreeting] = useState("");
  const [holdLine, setHoldLine] = useState(0);  
  const [script_content, setScriptContent] = useState("");
  const [additionalFields, setAdditionalFields] = useState([{ key: "", option: "", value: "" }]);
  const [preProcessingOptions, setPreProcessingOptions] = useState([]);  
  const [activeMainTab, setActiveMainTab] = useState("scriptContent");
  const [loading, setLoading] = useState(false);

 useEffect(() => {
  if (!isOpen) return;

  const loadScript = async () => {
    try {
      const optionsRes = await ApiService.getPreProcessingOptions();
      const optionsData = optionsRes?.data?.data || optionsRes?.data || [];
      const normalizedOptions = Array.isArray(optionsData)
        ? optionsData.map((opt) =>
            typeof opt === "string"
              ? { label: opt, value: opt }
              : { label: opt?.label ?? opt?.value ?? "", value: opt?.value ?? opt?.label ?? "" }
          )
        : [];
      setPreProcessingOptions(normalizedOptions);
    } catch (error) {
      console.error("Failed to load pre-processing options", error);
      setPreProcessingOptions([]);
    }

    // ✏️ EDIT MODE
    if (scriptToEdit) {
      try {
        setLoading(true);
        const res = await AuthService.getScriptById(scriptToEdit.id);
        const data = res?.data || res || {};

        setScriptName(data.script_name || "");
        setScriptDescription(data.script_description || "");
        setGreeting(data.greeting || "");
        setHoldLine(data.hold_line || "");
        setScriptContent(data.script_content || "");

        setAdditionalFields(
          data.additional_fields
            ? Object.entries(data.additional_fields).map(([key, value]) => ({
                key,
                option:
                  (typeof value === "object" && value !== null
                    ? value.pre_processing || value.preprocessing || value.option
                    : "") || data?.preprocessing_fields?.[key] || "",
                value:
                  typeof value === "object" && value !== null
                    ? value.value || ""
                    : value || "",
              }))
            : [{ key: "", option: "", value: "" }]
        );
      } catch (err) {
        console.error("Failed to load script", err);
      }
      return;
    }
    resetForm();
  };

  loadScript();
  setLoading(false);
}, [isOpen, scriptToEdit]);


const resetForm = () => {
  setScriptName("");
  setScriptDescription("");
  setGreeting("");
  setHoldLine("");
  setScriptContent("");
  setAdditionalFields([{ key: "", option: "", value: "" }]);
};


  const handleSave = () => {
    if (!script_name.trim()) {
      toast.error("Script name is required");
      return;
    }

    const additionalFieldsMap = {};
    const preprocessingFieldsMap = {};

    additionalFields.forEach((item) => {
      const key = item?.key?.trim();
      const value = item?.value?.trim();
      const option = String(item?.option || "").trim();
      if (!key) return;

      if (value) additionalFieldsMap[key] = value;
      if (option) preprocessingFieldsMap[key] = option;
    });

    const payload = {
      script_name: script_name.trim(),
      script_description: script_description.trim(),
      greeting: greeting.trim(),
     hold_line: Number(holdLine),   // ✅ send as integer
      script_content: script_content.trim(),
      additional_fields: additionalFieldsMap,
      preprocessing_fields: preprocessingFieldsMap,
      company_id: companyId,
      campaign_id: campaignId,
    };

    onSave(payload);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="add-script-modal-overlay">
      <div className="add-script-modal-content">

        {/* HEADER */}
        <div className="modal-header">
          <h2><Layers size={20} /> {scriptToEdit ? "Edit Script" : "Create New Script"}</h2>
          <button onClick={onClose} className="modal-close-btn"><X size={22} /></button>
        </div>

        {/* BODY */}
        <div className="modal-body">
          <div style={{ display: "flex", width: "100%", height: "100%", minHeight: 0 }}>

          {/* SIDEBAR */}
          <div className="modal-sidebar">

            <label className="modal-label">Script Name</label>
            <input
              type="text"
              placeholder="Enter script name"
              value={script_name}
              onChange={(e) => setScriptName(e.target.value)}
              className="modal-input"
            />

            <label className="modal-label">Description</label>
            <textarea
              placeholder="Enter description"
              value={script_description}
              onChange={(e) => setScriptDescription(e.target.value)}
              className="modal-input"
              style={{ height: "80px" }} /* Balanced comfortable description height */
            />

            <label className="modal-label">Greeting</label>
            <textarea
              placeholder="Hello! Thank you for calling..."
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
              className="modal-input"
              style={{ height: "160px" }} /* Restored dynamic greeting text space */
            />

            {/*  NEW FIELD - Converted to clean numeric input */}
            <label className="modal-label">Hold Line (seconds)</label>
            <input
                type="number"
                placeholder="Enter hold line duration"
                value={holdLine || ""}
                onChange={(e) => setHoldLine(parseInt(e.target.value) || 0)}
                className="modal-input"
              />

          </div>

          {/* MAIN CONTENT (Tabs) */}
          <div className="modal-main">

            <div className="main-tabs">
              <button
                className={`main-tab-btn ${activeMainTab === "scriptContent" ? "active" : ""}`}
                onClick={() => setActiveMainTab("scriptContent")}
              >
                Script Content
              </button>

              <button
                className={`main-tab-btn ${activeMainTab === "additionalFields" ? "active" : ""}`}
                onClick={() => setActiveMainTab("additionalFields")}
              >
                Additional Fields
              </button>
            </div>

            {activeMainTab === "scriptContent" && (
              <AceEditor
                mode="markdown"
                theme="github"
                width="100%"
                height="450px"
                fontSize={14}
                value={script_content}
                onChange={(val) => setScriptContent(val)}
                showPrintMargin={false}
                editorProps={{ $blockScrolling: true }}
                setOptions={{
                  useWorker: false,
                  showLineNumbers: true,
                }}
              />
            )}

            {activeMainTab === "additionalFields" && (
              <>
                <label className="modal-label" style={{ fontWeight: 700 }}>Additional Fields</label>

                {additionalFields.map((item, index) => (
                  <div key={index} style={{ display: "flex", gap: "6px", marginBottom: "8px" }}>
                    <input
                      type="text"
                      placeholder="key (customer_name)"
                      value={item.key}
                      disabled
                      onChange={(e) => {
                        const updated = [...additionalFields];
                        updated[index].key = e.target.value;
                        setAdditionalFields(updated);
                      }}
                      className="modal-input"
                      style={{ width: "35%" }}
                    />

                    <select
                      value={item.option || ""}
                      onChange={(e) => {
                        const updated = [...additionalFields];
                        updated[index].option = e.target.value;
                        setAdditionalFields(updated);
                      }}
                      className="modal-input"
                      style={{ width: "30%" }}
                    >
                      <option value="">Select option</option>
                      {preProcessingOptions.map((opt) => (
                        <option key={opt.value || opt.label} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>

                    <input
                      type="text"
                      placeholder="Value"
                      value={item.value}
                      onChange={(e) => {
                        const updated = [...additionalFields];
                        updated[index].value = e.target.value;
                        setAdditionalFields(updated);
                      }}
                      className="modal-input"
                      style={{ width: "35%" }}
                    />
                  </div>
                ))}

              </>
            )}
          </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <button className="modal-btn cancel" onClick={onClose}>Cancel</button>
          <button className="modal-btn save" onClick={handleSave}>
            {scriptToEdit ? "Update Script" : "Save Script"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddScriptModal;
